import { memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { FormContent, StyledDialog } from './SaveFilterModal.styles';
import { Button } from 'components/mui/common/Button';
import { TextField } from 'components/mui/common/TextField';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, isDefault: boolean) => void;
  isLoading?: boolean;
  initialName?: string;
  initialIsDefault?: boolean;
  allowDefault?: boolean;
  sx?: SxProps<Theme>;
}

export const SaveFilterModal = memo(
  ({ open, onClose, onSave, isLoading, initialName = '', initialIsDefault = false, allowDefault = true, sx }: Props) => {
    const { t } = useTranslation();
    const [filterName, setFilterName] = useState(initialName);
    const [isDefault, setIsDefault] = useState(initialIsDefault);

    useEffect(() => {
      if (open) {
        setFilterName(initialName);
        setIsDefault(initialIsDefault);
      }
    }, [open, initialName, initialIsDefault]);

    const handleSave = () => {
      if (filterName.trim()) {
        onSave(filterName.trim(), isDefault);
      }
    };

    return (
      <StyledDialog open={open} onClose={onClose} sx={sx}>
        <DialogTitle>{initialName ? t('helios.templates.filters.updateFilter') : t('helios.templates.filters.saveFilter')}</DialogTitle>
        <DialogContent>
          <FormContent>
            <TextField
              autoFocus
              fullWidth
              label={t('helios.templates.filters.filterName')}
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder={t('helios.templates.filters.filterNamePlaceholder')}
            />
            {allowDefault && (
              <FormControlLabel
                control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
                label={t('helios.templates.filters.setAsDefault')}
              />
            )}
          </FormContent>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!filterName.trim() || isLoading}>
            {initialName ? t('common.update') : t('common.save')}
          </Button>
        </DialogActions>
      </StyledDialog>
    );
  }
);

SaveFilterModal.displayName = 'SaveFilterModal';
